export interface BlockedRange {
  start_date: string;
  end_date: string;
}

export interface DailyRate {
  date: string;
  price: number;
}

const DAY_MS = 86_400_000;

function dateToUtcMs(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.slice(0, 10));
  if (!match) return null;
  const ms = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isFinite(ms) ? ms : null;
}

function utcMsToDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

export function expandBlockedRanges(ranges: BlockedRange[]): Set<string> {
  const days = new Set<string>();
  for (const range of ranges) {
    const start = dateToUtcMs(range.start_date);
    const end = dateToUtcMs(range.end_date);
    if (start === null || end === null || start > end) continue;
    for (let ms = start, count = 0; ms <= end && count < 800; ms += DAY_MS, count++) {
      days.add(utcMsToDate(ms));
    }
  }
  return days;
}

export function compressBlockedDays(days: Iterable<string>): BlockedRange[] {
  const sorted = [...new Set(days)].sort();
  const ranges: BlockedRange[] = [];
  for (const day of sorted) {
    const last = ranges[ranges.length - 1];
    const lastMs = last ? dateToUtcMs(last.end_date) : null;
    const dayMs = dateToUtcMs(day);
    if (last && lastMs !== null && dayMs === lastMs + DAY_MS) {
      last.end_date = day;
    } else if (dayMs !== null) {
      ranges.push({ start_date: day, end_date: day });
    }
  }
  return ranges;
}

export function mergeBlockedRangeDelta(args: {
  original: BlockedRange[];
  local: BlockedRange[];
  fresh: BlockedRange[];
}): { ranges: BlockedRange[]; expectedDates: string[] } {
  const original = expandBlockedRanges(args.original);
  const local = expandBlockedRanges(args.local);
  const fresh = expandBlockedRanges(args.fresh);
  const merged = new Set(fresh);

  for (const day of original) {
    if (!local.has(day)) merged.delete(day);
  }
  for (const day of local) {
    if (!original.has(day)) merged.add(day);
  }

  return {
    ranges: compressBlockedDays(merged),
    expectedDates: [...fresh].sort(),
  };
}

function rateMap(rates: DailyRate[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const rate of rates) {
    const date = rate.date.slice(0, 10);
    const price = Number(rate.price);
    if (/^\d{4}-\d{2}-\d{2}$/.test(date) && Number.isFinite(price) && price > 0) {
      map.set(date, price);
    }
  }
  return map;
}

function sortedRates(map: Map<string, number>): DailyRate[] {
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, price]) => ({ date, price }));
}

export function mergeDailyRateDelta(args: {
  original: DailyRate[];
  local: DailyRate[];
  fresh: DailyRate[];
}): { rates: DailyRate[]; expectedRates: DailyRate[] } {
  const original = rateMap(args.original);
  const local = rateMap(args.local);
  const fresh = rateMap(args.fresh);
  const merged = new Map(fresh);
  const dates = new Set([...original.keys(), ...local.keys()]);

  for (const date of dates) {
    const before = original.get(date);
    const after = local.get(date);
    if (before === after) continue;
    if (after === undefined) merged.delete(date);
    else merged.set(date, after);
  }

  return {
    rates: sortedRates(merged),
    expectedRates: sortedRates(fresh),
  };
}

export function applyDailyRateChanges(
  fresh: DailyRate[],
  changes: ReadonlyMap<string, number>,
): { rates: DailyRate[]; expectedRates: DailyRate[] } {
  const current = rateMap(fresh);
  const expectedRates = sortedRates(current);
  for (const [date, price] of changes) {
    if (Number.isFinite(price) && price > 0) current.set(date.slice(0, 10), price);
  }
  return { rates: sortedRates(current), expectedRates };
}